import { useMemo, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { Download } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { EntityPicker } from "@/components/shared/entity-picker";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  MasterSummaryStrip,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/features/clients/constants";
import {
  bucketLabel,
  entryDescription,
  useMasterLedger,
  type MasterLedgerEntry,
  type MasterPartyType,
} from "@/features/books/api";
import { EXPENSE_CATEGORIES, labelFor } from "@/features/expenses/constants";
import { splitDebitCredit } from "@/lib/party-ledger";
import { isSupabaseConfigured } from "@/lib/supabase";

const PARTY_FILTERS: { value: "all" | MasterPartyType; label: string }[] = [
  { value: "all", label: "All parties" },
  { value: "client", label: "Clients (AR)" },
  { value: "vendor", label: "Vendors (AP)" },
  { value: "expense", label: "Expenses" },
];

const BUCKET_FILTERS: { value: "all" | MasterLedgerEntry["bucket"]; label: string }[] = [
  { value: "all", label: "All types" },
  { value: "sale", label: "Sales" },
  { value: "purchase", label: "Purchases" },
  { value: "receipt", label: "Receipts" },
  { value: "payment", label: "Payments" },
  { value: "expense", label: "Expenses" },
  { value: "credit", label: "Credit/debit notes" },
  { value: "opening", label: "Openings" },
  { value: "adjustment", label: "Adjustments" },
];

export function MasterLedgerPage() {
  return (
    <ErrorBoundary title="Master ledger crashed">
      <MasterLedgerInner />
    </ErrorBoundary>
  );
}

function MasterLedgerInner() {
  const { data = [], isLoading, error, refetch, isFetching } = useMasterLedger();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [partyType, setPartyType] = useState<"all" | MasterPartyType>("all");
  const [partyKey, setPartyKey] = useState("all");
  const [bucket, setBucket] = useState<"all" | MasterLedgerEntry["bucket"]>("all");
  const [search, setSearch] = useState("");

  useMasterListShortcuts({ searchInputId: "books-search", enabled: true });

  const partyOptions = useMemo(() => {
    const map = new Map<string, { key: string; label: string; keywords: string }>();
    for (const row of data) {
      if (partyType !== "all" && row.party_type !== partyType) continue;
      const key = `${row.party_type}:${row.party_id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: `${row.party_code} · ${row.party_name}`,
          keywords: `${row.party_code} ${row.party_name} ${row.party_type}`,
        });
      }
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [data, partyType]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      if (partyType !== "all" && row.party_type !== partyType) return false;
      if (partyKey !== "all") {
        const [type, id] = partyKey.split(":");
        if (row.party_type !== type || row.party_id !== id) return false;
      }
      if (bucket !== "all" && row.bucket !== bucket) return false;
      if (from && row.movement_date < from) return false;
      if (to && row.movement_date > to) return false;
      if (q) {
        const hay = [
          row.party_name,
          row.party_code,
          row.kind,
          row.reference,
          row.notes,
          bucketLabel(row.bucket),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, partyType, partyKey, bucket, from, to, search]);

  const displayRows = useMemo(
    () =>
      filtered
        .slice()
        .sort((a, b) => b.movement_date.localeCompare(a.movement_date) || b.id.localeCompare(a.id)),
    [filtered],
  );

  const stats = useMemo(() => {
    let sales = 0;
    let purchases = 0;
    let receipts = 0;
    let payments = 0;
    let expenses = 0;
    let ar = 0;
    let ap = 0;
    let periodDebit = 0;
    let periodCredit = 0;

    for (const row of filtered) {
      const { debit, credit } = splitDebitCredit(row.amount);
      periodDebit += debit;
      periodCredit += credit;
      if (row.party_type === "client") ar += row.amount;
      if (row.party_type === "vendor") ap += row.amount;
      if (row.bucket === "sale" || (row.party_type === "client" && row.kind === "invoice")) {
        sales += Math.abs(row.amount);
      }
      if (row.bucket === "purchase" || (row.party_type === "vendor" && row.kind === "bill")) {
        purchases += Math.abs(row.amount);
      }
      if (row.bucket === "receipt") receipts += Math.abs(row.amount);
      if (row.bucket === "payment") payments += Math.abs(row.amount);
      if (row.bucket === "expense" || row.party_type === "expense") {
        expenses += Math.abs(row.amount);
      }
    }

    return { sales, purchases, receipts, payments, expenses, ar, ap, periodDebit, periodCredit };
  }, [filtered]);

  function exportCsv() {
    const header = [
      "date",
      "party_type",
      "party_code",
      "party_name",
      "bucket",
      "kind",
      "description",
      "reference",
      "debit",
      "credit",
      "signed_amount",
    ];
    const body = displayRows.map((row) => {
      const { debit, credit } = splitDebitCredit(row.amount);
      return [
        row.movement_date,
        row.party_type,
        csv(row.party_code),
        csv(row.party_name),
        row.bucket,
        row.kind,
        csv(entryDescription(row)),
        csv(row.reference ?? ""),
        debit || "",
        credit || "",
        row.amount,
      ].join(",");
    });
    const blob = new Blob([[header.join(","), ...body].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `master-ledger-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function clearFilters() {
    setFrom("");
    setTo("");
    setPartyType("all");
    setPartyKey("all");
    setBucket("all");
    setSearch("");
  }

  const filtersActive =
    from || to || partyType !== "all" || partyKey !== "all" || bucket !== "all" || search;

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to load the master ledger."
      />
    );
  }

  return (
    <MasterPage>
      <MasterHeader
        title="Master ledger"
        countLabel="Company books"
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={!displayRows.length}
          >
            <Download />
            Export
          </Button>
        }
      />

      <MasterSummaryStrip
        items={[
          { label: "Sales", value: formatMoney(stats.sales) },
          { label: "Purchases", value: formatMoney(stats.purchases) },
          { label: "Receipts", value: formatMoney(stats.receipts), tone: "success" },
          { label: "Payments", value: formatMoney(stats.payments) },
          { label: "Expenses", value: formatMoney(stats.expenses), tone: "warning" },
          { label: "AR to receive", value: formatMoney(Math.max(0, stats.ar)) },
          { label: "AP to pay", value: formatMoney(Math.max(0, stats.ap)), tone: "warning" },
        ]}
      />

      <MasterFilterBar shownLabel={`${displayRows.length} shown`}>
        <FilterField label="From">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-32" />
        </FilterField>
        <FilterField label="To">
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-32" />
        </FilterField>
        <FilterField label="Party type">
          <NativeSelect
            value={partyType}
            onChange={(e) => {
              setPartyType(e.target.value as "all" | MasterPartyType);
              setPartyKey("all");
            }}
            className="w-36"
          >
            {PARTY_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </NativeSelect>
        </FilterField>
        <FilterField label="Party">
          <div className="w-52">
            <EntityPicker
              allowClear
              value={partyKey === "all" ? "" : partyKey}
              placeholder="All parties…"
              recentKey="books-party"
              options={partyOptions.map((p) => ({
                id: p.key,
                label: p.label,
                keywords: p.keywords,
              }))}
              onChange={(id) => setPartyKey(id || "all")}
            />
          </div>
        </FilterField>
        <FilterField label="Type">
          <NativeSelect
            value={bucket}
            onChange={(e) => setBucket(e.target.value as "all" | MasterLedgerEntry["bucket"])}
            className="w-36"
          >
            {BUCKET_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </NativeSelect>
        </FilterField>
        <MasterSearchInput
          id="books-search"
          value={search}
          onChange={setSearch}
          placeholder="Name, ref…  (/)"
          className="self-end"
        />
        {filtersActive ? (
          <Button type="button" variant="ghost" size="xs" className="self-end" onClick={clearFilters}>
            Clear
          </Button>
        ) : null}
      </MasterFilterBar>

      {error ? <MasterBanner tone="danger">{(error as Error).message}</MasterBanner> : null}

      {isLoading ? (
        <p className="py-12 text-center text-xs text-muted-foreground">Loading ledger…</p>
      ) : displayRows.length === 0 ? (
        <EmptyState
          title="No entries match"
          description="Adjust filters, add expenses, or set openings / adjustments on party ledgers."
        />
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Date</TableHead>
                <TableHead>Party</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right text-primary">Debit</TableHead>
                <TableHead className="text-right text-success">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRows.map((row) => {
                const { debit, credit } = splitDebitCredit(row.amount);
                return (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap tabular-nums text-xs">
                      {format(parseISO(row.movement_date), "dd MMM yyyy")}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge
                          variant={
                            row.party_type === "client"
                              ? "default"
                              : row.party_type === "vendor"
                                ? "secondary"
                                : "outline"
                          }
                        >
                          {row.party_type === "client"
                            ? "Client"
                            : row.party_type === "vendor"
                              ? "Vendor"
                              : "Expense"}
                        </Badge>
                        {row.party_type === "client" ? (
                          <Link
                            to="/clients/$clientId/statement"
                            params={{ clientId: row.party_id }}
                            className="text-xs font-medium text-primary hover:underline"
                          >
                            {row.party_name}
                          </Link>
                        ) : row.party_type === "vendor" ? (
                          <Link
                            to="/vendors/$vendorId/ledger"
                            params={{ vendorId: row.party_id }}
                            className="text-xs font-medium text-primary hover:underline"
                          >
                            {row.party_name}
                          </Link>
                        ) : (
                          <Link
                            to="/expenses"
                            className="text-xs font-medium text-primary hover:underline"
                          >
                            {row.party_name}
                          </Link>
                        )}
                      </div>
                      <div className="font-mono text-2xs text-muted-foreground">{row.party_code}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs font-medium">{bucketLabel(row.bucket)}</div>
                      <div className="text-2xs text-muted-foreground">
                        {row.party_type === "expense"
                          ? labelFor(EXPENSE_CATEGORIES, row.kind)
                          : row.kind}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[16rem] truncate text-xs">
                      {entryDescription(row)}
                    </TableCell>
                    <TableCell className="font-mono text-2xs text-muted-foreground">
                      {row.reference || "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-primary">
                      {debit ? formatMoney(debit) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-success">
                      {credit ? formatMoney(credit) : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between border-t border-border px-3 py-1.5 text-2xs text-muted-foreground">
            <span>
              {displayRows.length} entr{displayRows.length === 1 ? "y" : "ies"}
            </span>
            <span>
              Debit {formatMoney(stats.periodDebit)} · Credit {formatMoney(stats.periodCredit)}
            </span>
          </div>
        </div>
      )}
    </MasterPage>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-0.5">
      <span className="field-label">{label}</span>
      {children}
    </div>
  );
}

function csv(value: string) {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
